import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  Mail, Send, Clock, CheckCircle, AlertCircle, Loader2, ChevronDown, ChevronUp,
  Inbox, Plus, Trash2, ArrowDownCircle, ArrowUpCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { DateInput } from "../components/ui/date-input";

const Correspondence = () => {
  const [activeTab, setActiveTab] = useState("outgoing");
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [courtType, setCourtType] = useState("Μονομελές");
  const [sending, setSending] = useState(false);
  const [smtpAvailable, setSmtpAvailable] = useState(false);
  const [smtpConfigs, setSmtpConfigs] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);

  // Incoming mail state
  const [incomingMails, setIncomingMails] = useState([]);
  const [incomingTotal, setIncomingTotal] = useState(0);
  const [loadingIncoming, setLoadingIncoming] = useState(true);
  const [showIncomingForm, setShowIncomingForm] = useState(false);
  const [incSender, setIncSender] = useState("");
  const [incSubject, setIncSubject] = useState("");
  const [incBody, setIncBody] = useState("");
  const [incDate, setIncDate] = useState("");
  const [incProtocol, setIncProtocol] = useState("");
  const [incCourtType, setIncCourtType] = useState("");
  const [savingIncoming, setSavingIncoming] = useState(false);
  const [expandedIncoming, setExpandedIncoming] = useState(null);

  useEffect(() => { checkSmtp(); fetchHistory(); fetchIncoming(); }, []);

  const checkSmtp = async () => {
    try {
      const r = await axios.get(`${API}/correspondence/smtp-available`);
      setSmtpAvailable(r.data.available);
      setSmtpConfigs(r.data.configs || []);
      if (r.data.configs?.length > 0) setCourtType(r.data.configs[0].court_type);
    } catch { /* ignore */ }
  };

  const fetchHistory = async () => {
    try {
      setLoadingHistory(true);
      const r = await axios.get(`${API}/correspondence?limit=50`);
      setHistory(r.data.items || []);
      setHistoryTotal(r.data.total_count || 0);
    } catch { /* ignore */ }
    finally { setLoadingHistory(false); }
  };

  const fetchIncoming = async () => {
    try {
      setLoadingIncoming(true);
      const r = await axios.get(`${API}/correspondence/incoming?limit=50`);
      setIncomingMails(r.data.items || []);
      setIncomingTotal(r.data.total_count || 0);
    } catch { /* ignore */ }
    finally { setLoadingIncoming(false); }
  };

  const handleSend = async () => {
    if (!toEmail || !subject || !body) {
      toast.error("Συμπληρώστε όλα τα πεδία");
      return;
    }
    try {
      setSending(true);
      await axios.post(`${API}/correspondence/send`, { to_email: toEmail, subject, body, court_type: courtType });
      toast.success("Το email στάλθηκε επιτυχώς");
      setToEmail(""); setSubject(""); setBody("");
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία αποστολής email");
    } finally { setSending(false); }
  };

  const handleSaveIncoming = async () => {
    if (!incSender || !incSubject) {
      toast.error("Συμπληρώστε αποστολέα και θέμα");
      return;
    }
    try {
      setSavingIncoming(true);
      await axios.post(`${API}/correspondence/incoming`, {
        sender: incSender, subject: incSubject, body: incBody,
        received_date: incDate, protocol_number: incProtocol, court_type: incCourtType
      });
      toast.success("Το εισερχόμενο καταγράφηκε");
      setIncSender(""); setIncSubject(""); setIncBody("");
      setIncDate(""); setIncProtocol(""); setIncCourtType("");
      setShowIncomingForm(false);
      fetchIncoming();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα καταγραφής");
    } finally { setSavingIncoming(false); }
  };

  const handleDeleteIncoming = async (mailId) => {
    if (!window.confirm("Διαγραφή εισερχομένου;")) return;
    try {
      await axios.delete(`${API}/correspondence/incoming/${mailId}`);
      toast.success("Διαγράφηκε");
      fetchIncoming();
    } catch { toast.error("Σφάλμα διαγραφής"); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return dateStr; }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="correspondence-title">Αλληλογραφία</h1>
          <p className="text-slate-500 mt-1">Εξερχόμενα & Εισερχόμενα</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="correspondence-tabs">
        <button
          onClick={() => setActiveTab("outgoing")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === "outgoing" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="tab-outgoing"
        >
          <ArrowUpCircle className="w-4 h-4" />
          Εξερχόμενα
          {historyTotal > 0 && <Badge variant="secondary" className="text-xs">{historyTotal}</Badge>}
        </button>
        <button
          onClick={() => setActiveTab("incoming")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === "incoming" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="tab-incoming"
        >
          <ArrowDownCircle className="w-4 h-4" />
          Εισερχόμενα
          {incomingTotal > 0 && <Badge variant="secondary" className="text-xs">{incomingTotal}</Badge>}
        </button>
      </div>

      {/* OUTGOING TAB */}
      {activeTab === "outgoing" && (
        <>
          {!smtpAvailable ? (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-6 text-center">
                <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
                <h3 className="font-medium text-amber-800">Δεν έχουν ρυθμιστεί SMTP</h3>
                <p className="text-sm text-amber-600 mt-1">Ρυθμίστε τα SMTP στο Πίνακα Διαχείρισης</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base"><Send className="w-5 h-5" />Νέο Email</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Προς (Email)</Label>
                    <Input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)}
                      placeholder="email@example.com" data-testid="to-email-input" />
                  </div>
                  <div>
                    <Label>Αποστολή μέσω SMTP</Label>
                    <Select value={courtType} onValueChange={setCourtType}>
                      <SelectTrigger data-testid="smtp-court-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {smtpConfigs.map(c => (
                          <SelectItem key={c.court_type} value={c.court_type}>
                            {c.court_type} ({c.sender_email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Θέμα</Label>
                  <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Θέμα email" data-testid="subject-input" />
                </div>
                <div>
                  <Label>Μήνυμα</Label>
                  <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Γράψτε το μήνυμά σας..." rows={6} data-testid="body-input" />
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSend} disabled={sending || !toEmail || !subject || !body} data-testid="send-email-btn">
                    {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Αποστολή...</> : <><Send className="w-4 h-4 mr-2" />Αποστολή Email</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Outgoing History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ArrowUpCircle className="w-5 h-5" />
                Ιστορικό Εξερχομένων
                {historyTotal > 0 && <Badge variant="outline">{historyTotal}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>Δεν υπάρχει ιστορικό εξερχομένων</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map((item) => (
                    <div key={item.id} className="border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                        data-testid={`history-item-${item.id}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {item.status === "sent" ? <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{item.subject}</p>
                            <p className="text-xs text-slate-500">Προς: {item.to_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(item.sent_at)}</span>
                          {expandedItem === item.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </div>
                      </div>
                      {expandedItem === item.id && (
                        <div className="px-3 pb-3 pt-1 border-t bg-slate-50">
                          <div className="text-sm text-slate-700 whitespace-pre-wrap">{item.body}</div>
                          {item.error && <p className="text-xs text-red-500 mt-2">Σφάλμα: {item.error}</p>}
                          <div className="text-xs text-slate-400 mt-2">Αποστολέας: {item.sender_name} | SMTP: {item.court_type}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* INCOMING TAB */}
      {activeTab === "incoming" && (
        <>
          {/* New incoming form */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <ArrowDownCircle className="w-5 h-5" />
                Καταγραφή Εισερχομένου
              </CardTitle>
              <Button variant={showIncomingForm ? "ghost" : "default"} size="sm"
                onClick={() => setShowIncomingForm(!showIncomingForm)} data-testid="toggle-incoming-form">
                {showIncomingForm ? "Κλείσιμο" : <><Plus className="w-4 h-4 mr-1" />Νέο</>}
              </Button>
            </CardHeader>
            {showIncomingForm && (
              <CardContent className="space-y-4 border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Αποστολέας *</Label>
                    <Input value={incSender} onChange={e => setIncSender(e.target.value)} placeholder="π.χ. Υπουργείο Δικαιοσύνης" data-testid="inc-sender" />
                  </div>
                  <div>
                    <Label>Αρ. Πρωτοκόλλου</Label>
                    <Input value={incProtocol} onChange={e => setIncProtocol(e.target.value)} placeholder="π.χ. 12345/2026" data-testid="inc-protocol" />
                  </div>
                  <div>
                    <Label>Ημ. Λήψης</Label>
                    <DateInput value={incDate} onChange={e => setIncDate(e.target.value)} data-testid="inc-date" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Θέμα *</Label>
                    <Input value={incSubject} onChange={e => setIncSubject(e.target.value)} placeholder="Θέμα εγγράφου" data-testid="inc-subject" />
                  </div>
                  <div>
                    <Label>Τύπος Δικαστηρίου</Label>
                    <Select value={incCourtType} onValueChange={setIncCourtType}>
                      <SelectTrigger data-testid="inc-court-type"><SelectValue placeholder="Επιλέξτε" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Μονομελές">Μονομελές</SelectItem>
                        <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
                        <SelectItem value="Γενικό">Γενικό</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Σημειώσεις / Περιεχόμενο</Label>
                  <Textarea value={incBody} onChange={e => setIncBody(e.target.value)} placeholder="Προαιρετικές σημειώσεις..." rows={3} data-testid="inc-body" />
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveIncoming} disabled={savingIncoming || !incSender || !incSubject} data-testid="save-incoming-btn">
                    {savingIncoming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Αποθήκευση...</> : <><Plus className="w-4 h-4 mr-2" />Καταγραφή</>}
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Incoming list */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Inbox className="w-5 h-5" />
                Εισερχόμενα
                {incomingTotal > 0 && <Badge variant="outline">{incomingTotal}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingIncoming ? (
                <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
              ) : incomingMails.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>Δεν υπάρχουν εισερχόμενα</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {incomingMails.map((item) => (
                    <div key={item.id} className="border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedIncoming(expandedIncoming === item.id ? null : item.id)}
                        data-testid={`incoming-item-${item.id}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <ArrowDownCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{item.subject}</p>
                            <p className="text-xs text-slate-500">Από: {item.sender}{item.protocol_number ? ` | Αρ. Πρωτ.: ${item.protocol_number}` : ""}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          {item.court_type && <Badge variant="outline" className="text-xs">{item.court_type}</Badge>}
                          <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(item.received_date)}</span>
                          {expandedIncoming === item.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </div>
                      </div>
                      {expandedIncoming === item.id && (
                        <div className="px-3 pb-3 pt-1 border-t bg-blue-50/50">
                          {item.body && <div className="text-sm text-slate-700 whitespace-pre-wrap">{item.body}</div>}
                          <div className="flex justify-between items-center mt-2">
                            <div className="text-xs text-slate-400">Καταγράφηκε από: {item.logged_by}</div>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7" 
                              onClick={(e) => { e.stopPropagation(); handleDeleteIncoming(item.id); }}
                              data-testid={`delete-incoming-${item.id}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" />Διαγραφή
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Correspondence;
